// page-bugs.ts — Suivi des bugs (côté utilisateur) : liste de SES bugs.
//
// CTA « Nouveau bug », bloc de filtres (état / priorité / urgence), table triable
// (déclarant, id, titre, date, état, priorité, urgence, version de résolution) +
// CTA ✎ modifier / ✕ supprimer. Clic sur id ou titre = ouvrir la page de MAJ.
// Le trigramme du déclarant n'est pas un filtre ici : la liste est déjà bornée
// aux bugs de l'utilisateur courant (scoping serveur).

import { navigateTo } from '../router.js';

interface BugRow {
  id: number;
  reporter_trigram: string | null;
  title: string;
  state_code: string;
  state_label: string;
  priority_code: string;
  priority_label: string;
  urgency_code: string;
  urgency_label: string;
  resolution_version: string | null;
  created_at: string | null;
  messages_count: number;
}

interface RefItem {
  code: string;
  label: string;
  is_final?: boolean;
}
interface Referentials {
  priorities: RefItem[];
  urgencies: RefItem[];
  states: RefItem[];
}
interface BugsFilters {
  state_code?: string;
  priority_code?: string;
  urgency_code?: string;
  include_resolved?: boolean;
  is_evolution?: boolean;
}

type SortKey =
  | 'reporter_trigram'
  | 'id'
  | 'title'
  | 'created_at'
  | 'state_label'
  | 'priority_label'
  | 'urgency_label'
  | 'resolution_version';

let _bugRef: Referentials | null = null;
let _bugs: BugRow[] = [];
let _filters: BugsFilters = {};
let _sortKey: SortKey = 'id';
let _sortDir: 'asc' | 'desc' = 'desc';

async function _loadBugRef(): Promise<Referentials> {
  if (!_bugRef) _bugRef = (await api.getBugReferentials()) as Referentials;
  return _bugRef;
}

function _fmtDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s.endsWith('Z') ? s : s + 'Z');
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('fr-FR');
}

function _opts(items: RefItem[], selected: string | undefined, allLabel?: string): string {
  const all = allLabel ? `<option value="">${esc(allLabel)}</option>` : '';
  return (
    all +
    items
      .map(
        (i) =>
          `<option value="${esc(i.code)}"${i.code === selected ? ' selected' : ''}>${esc(i.label)}</option>`,
      )
      .join('')
  );
}

function _sortBugs(arr: ReadonlyArray<BugRow>): BugRow[] {
  return [...arr].sort((a, b) => {
    const va = a[_sortKey];
    const vb = b[_sortKey];
    const na = va == null ? '' : va;
    const nb = vb == null ? '' : vb;
    const cmp = na < nb ? -1 : na > nb ? 1 : 0;
    return cmp * (_sortDir === 'asc' ? 1 : -1);
  });
}

function _th(key: SortKey, label: string): string {
  const active = _sortKey === key;
  const arrow = active ? (_sortDir === 'asc' ? '↑' : '↓') : '↕';
  return `<th data-sort="${key}" style="cursor:pointer;user-select:none">${esc(label)} <span style="font-size:10px;color:var(--text3)">${arrow}</span></th>`;
}

function _row(b: BugRow): string {
  return `<tr data-bug-id="${b.id}">
    <td>${esc(b.reporter_trigram || '—')}</td>
    <td style="cursor:pointer" data-open-bug="${b.id}"><strong>#${b.id}</strong></td>
    <td style="cursor:pointer" data-open-bug="${b.id}">${esc(b.title)}</td>
    <td style="font-size:12px">${esc(_fmtDate(b.created_at))}</td>
    <td>${esc(b.state_label)}</td>
    <td>${esc(b.priority_label)}</td>
    <td>${esc(b.urgency_label)}</td>
    <td>${esc(b.resolution_version || '—')}</td>
    <td style="white-space:nowrap">
      <button class="btn btn-secondary btn-icon" data-edit-bug="${b.id}" title="${esc(t('common.edit'))}" style="color:#147A57">✎</button>
      <button class="btn btn-danger btn-icon" data-del-bug="${b.id}" style="margin-left:4px" title="${esc(t('common.delete'))}">✕</button>
    </td>
  </tr>`;
}

function _tbody(): string {
  const rows = _sortBugs(_bugs).map(_row).join('');
  return rows || `<tr><td colspan="9" class="empty">${esc(t('bugs.empty'))}</td></tr>`;
}

// Mémorise l'ordre courant (filtres + tri) des ids de bugs, pour que la page de
// détail propose « bug précédent / suivant » fidèle à la liste. Clé d'origine
// (sous-domaine = tenant) → naturellement isolée par tenant.
const BUGS_ORDER_KEY = 'ornexia:bugs:order';
function _persistOrder(): void {
  try {
    sessionStorage.setItem(BUGS_ORDER_KEY, JSON.stringify(_sortBugs(_bugs).map((b) => b.id)));
  } catch {
    /* sessionStorage indisponible : la nav prev/next retombera sur un fetch */
  }
}

async function _reload(): Promise<void> {
  _bugs = (await api.getBugs(_filters)) as BugRow[];
  const tb = document.getElementById('bugs-tbody');
  if (tb) tb.innerHTML = _tbody();
  _persistOrder();
}

function _renderList(ref: Referentials): void {
  const filterBar = `<div style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:12px;align-items:flex-end">
    <div class="form-group" style="margin:0"><label>${esc(t('bugs.col.state'))}</label>
      <select id="flt-state">${_opts(ref.states, _filters.state_code, t('bugs.filter.allM'))}</select></div>
    <div class="form-group" style="margin:0"><label>${esc(t('bugs.col.priority'))}</label>
      <select id="flt-priority">${_opts(ref.priorities, _filters.priority_code, t('bugs.filter.allF'))}</select></div>
    <div class="form-group" style="margin:0"><label>${esc(t('bugs.col.urgency'))}</label>
      <select id="flt-urgency">${_opts(ref.urgencies, _filters.urgency_code, t('bugs.filter.allF'))}</select></div>
    <div class="form-group" style="margin:0"><label>${esc(t('bugs.filter.type'))}</label>
      <select id="flt-type">
        <option value="">${esc(t('bugs.filter.allM'))}</option>
        <option value="bug"${_filters.is_evolution === false ? ' selected' : ''}>${esc(t('bugs.filter.typeBug'))}</option>
        <option value="evol"${_filters.is_evolution === true ? ' selected' : ''}>${esc(t('bugs.filter.typeEvol'))}</option>
      </select></div>
    <label style="display:flex;align-items:center;gap:6px;margin:0">
      <input type="checkbox" id="flt-resolved"${_filters.include_resolved ? ' checked' : ''}> ${esc(t('bugs.filter.includeResolved'))}</label>
  </div>`;

  const table = `<div class="table-wrap"><table id="bugs-table">
    <thead><tr>
      ${_th('reporter_trigram', t('bugs.col.reporter'))}
      ${_th('id', t('bugs.col.id'))}
      ${_th('title', t('bugs.col.title'))}
      ${_th('created_at', t('bugs.col.created'))}
      ${_th('state_label', t('bugs.col.state'))}
      ${_th('priority_label', t('bugs.col.priority'))}
      ${_th('urgency_label', t('bugs.col.urgency'))}
      ${_th('resolution_version', t('bugs.col.resolution'))}
      <th></th>
    </tr></thead>
    <tbody id="bugs-tbody">${_tbody()}</tbody>
  </table></div>`;

  setContent(`<div class="card">${filterBar}${table}</div>`);
  _persistOrder();

  const onFilter = (
    key: 'state_code' | 'priority_code' | 'urgency_code',
    el: HTMLSelectElement | null,
  ): void => {
    el?.addEventListener('change', () => {
      const v = el.value;
      if (v) _filters[key] = v;
      else delete _filters[key];
      void _reload();
    });
  };
  onFilter('state_code', document.getElementById('flt-state') as HTMLSelectElement | null);
  onFilter('priority_code', document.getElementById('flt-priority') as HTMLSelectElement | null);
  onFilter('urgency_code', document.getElementById('flt-urgency') as HTMLSelectElement | null);
  document.getElementById('flt-type')?.addEventListener('change', (e: Event) => {
    const v = (e.target as HTMLSelectElement).value;
    if (v === 'evol') _filters.is_evolution = true;
    else if (v === 'bug') _filters.is_evolution = false;
    else delete _filters.is_evolution;
    void _reload();
  });
  document.getElementById('flt-resolved')?.addEventListener('change', (e: Event) => {
    const checked = (e.target as HTMLInputElement).checked;
    if (checked) _filters.include_resolved = true;
    else delete _filters.include_resolved;
    void _reload();
  });

  document.getElementById('bugs-table')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const sortTh = target.closest<HTMLElement>('[data-sort]');
    if (sortTh) {
      const key = sortTh.dataset['sort'] as SortKey;
      if (_sortKey === key) _sortDir = _sortDir === 'asc' ? 'desc' : 'asc';
      else {
        _sortKey = key;
        _sortDir = 'asc';
      }
      _renderList(ref);
      return;
    }
    const open = target.closest<HTMLElement>('[data-open-bug]');
    const edit = target.closest<HTMLElement>('[data-edit-bug]');
    if (open && !target.closest('button')) {
      navigateTo('bug/:id', { id: parseInt(open.dataset['openBug'] || '0') });
      return;
    }
    if (edit) {
      navigateTo('bug/:id', { id: parseInt(edit.dataset['editBug'] || '0') });
      return;
    }
    const del = target.closest<HTMLElement>('[data-del-bug]');
    if (del) {
      const id = parseInt(del.dataset['delBug'] || '0');
      _deleteBug(id);
    }
  });
}

function _deleteBug(id: number): void {
  const bug = _bugs.find((b) => b.id === id);
  const ref = '#' + id + (bug ? ' — ' + bug.title : '');
  openModal(
    t('bugs.delete.title'),
    `<p>${t('bugs.delete.confirm', { ref })}</p>`,
    async () => {
      try {
        await api.deleteBug(id);
        closeModal();
        toast(t('bugs.toast.deleted'));
        void _reload();
      } catch (err: unknown) {
        // 409 si le bug a des messages → le backend suggère de le passer à « Fermé ».
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    t('common.delete'),
  );
}

// « Nouveau bug + » ouvre un formulaire vierge (id sentinelle 0). Le bug n'est
// créé en base qu'au premier « Enregistrer » (cf. page-bug). Avant (bug #41), il
// était créé dès le clic : ressortir sans saisir laissait un bug vide orphelin.
function _openNew(): void {
  navigateTo('bug/:id', { id: 0 });
}

pages.bugs = async function () {
  setHeader(
    t('bugs.pageTitle'),
    `<button class="btn btn-primary" id="btn-new-bug">${esc(t('bugs.btnNew'))}</button>`,
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  // Câblé AVANT les deux fetch : le CTA est affiché par setHeader dès
  // maintenant, et il restait inerte pendant tout le chargement de la liste
  // (un clic dans cette fenêtre ne faisait rien — e2e rouge par intermittence
  // sur #bg-title, 2026-09-08). Déclarer un bug ne dépend d'aucune donnée
  // chargée ici : `_openNew` ne fait que naviguer vers la fiche vierge.
  document.getElementById('btn-new-bug')?.addEventListener('click', () => _openNew());
  try {
    const ref = await _loadBugRef();
    _bugs = (await api.getBugs(_filters)) as BugRow[];
    _renderList(ref);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

export {};
